import { useCallback, useEffect, useRef } from 'react';

/** The history state key that marks an open overlay's entry. */
export const OVERLAY_HISTORY_KEY = 'qmOverlay';

export function overlayMarkOf(state: unknown): string | null {
    if (!state || typeof state !== 'object') return null;
    const mark = (state as Record<string, unknown>)[OVERLAY_HISTORY_KEY];
    return typeof mark === 'string' ? mark : null;
}

/**
 * Back closes an overlay instead of leaving the page under it (DEC-AU-19):
 * the phone's Back button or gesture, and desktop Back.
 *
 * Opening adds one history entry, a copy of the current one with an overlay
 * mark, so the page's own popstate readers see the same page. Back takes that
 * entry off and calls `onBack`. `onBack` returns false when the overlay cannot
 * close yet (a save is running), and the entry goes back on.
 *
 * The overlay's own close calls the returned function first, which takes the
 * entry off too, so Back is not spent on a closed overlay. An overlay closed
 * by its page to open something else leaves the entry for that page to take
 * over (useIntakeJobNavigation replaces it with the job it opens).
 */
export function useBackToClose(onBack: () => boolean): () => void {
    const onBackRef = useRef(onBack);
    onBackRef.current = onBack;
    const markRef = useRef<string | null>(null);
    if (markRef.current === null) markRef.current = `overlay-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    // Set while the overlay takes its own entry off: that pop is not a Back.
    const leavingRef = useRef(false);

    useEffect(() => {
        const mark = markRef.current;
        const ours = () => overlayMarkOf(window.history.state) === mark;
        const add = () => {
            try {
                window.history.pushState({ ...(window.history.state || {}), [OVERLAY_HISTORY_KEY]: mark }, '', window.location.href);
            } catch { /* sandboxed contexts: Back then behaves as before */ }
        };
        // A re-run of this effect (StrictMode) finds its entry already there.
        if (!ours()) add();
        const onPop = () => {
            if (leavingRef.current) {
                leavingRef.current = false;
                return;
            }
            if (ours()) return;
            if (!onBackRef.current()) add();
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    return useCallback(() => {
        if (overlayMarkOf(window.history.state) !== markRef.current) return;
        leavingRef.current = true;
        window.history.back();
    }, []);
}
